import React, { useState, useEffect } from "react";
import FastAPIClient from "../../client";
import fast_api_config from "../../config";
import { useIntl } from "react-intl";
import UserSearch from "../Search/UserSearch";
import { MeetingType } from "../../types/meeting";
import { UserType } from "../../types/user";
import { FiWifiOff } from "react-icons/fi";

// Instantiate the FastAPI client
const client = new FastAPIClient(fast_api_config);

type UserListType = {
    user: UserType;
    randomColor: string;
};

interface SharedCalendarProps {
    currentUser: UserType;
    activeCalendars: string[];
    setActiveCalendars: React.Dispatch<React.SetStateAction<string[]>>;
    originalMeetings: MeetingType[];
    setOriginalMeetings: React.Dispatch<React.SetStateAction<MeetingType[]>>;
    usersList: UserListType[];
    setUsersList: React.Dispatch<React.SetStateAction<UserListType[]>>;
    setUsersListOriginal: React.Dispatch<React.SetStateAction<UserListType[]>>;
    calendarLoading: boolean;
    isOnline: boolean;
}

const SharedCalendarList: React.FC<SharedCalendarProps> = ({
    currentUser,
    activeCalendars,
    setActiveCalendars,
    setOriginalMeetings,
    usersList,
    setUsersList,
    setUsersListOriginal,
    calendarLoading,
    isOnline,
}) => {
    const intl = useIntl();
    const [shownList, setShownList] = useState<UserListType[]>([]);
    const [sharedMeetings, setSharedMeetings] = useState([]);

    useEffect(() => {
        if (activeCalendars.length === 0) {
            setActiveCalendars([currentUser.user_id]); // Only initialize if empty
        }
    }, []);

    const handleChange = (id: string) => {
        if (!activeCalendars.includes(id)) {
            setActiveCalendars((prevData) => [...prevData, id]);
        } else {
            setActiveCalendars((prevData) =>
                prevData.filter((cal) => cal !== id)
            );
        }
    };

    const reinitialize = () => {
        setActiveCalendars([currentUser.user_id]);
        setUsersList((prev) => [...prev, ...shownList]);
        setShownList([]);
    };
    const getMeetings = async (id: string) => {
        const data = await client.getMeetings(id);
        setSharedMeetings((prevMeetings) => [
            ...prevMeetings,
            data.data?.results,
        ]);
    };

    const handleRemoveUser = (user: UserListType) => {
        setUsersList((prevList) => [...prevList, user]);
        const filterShownList = [...shownList].filter(
            (el) => el.user.user_id != user.user.user_id
        );
        setShownList(filterShownList);
        handleChange(user.user.user_id);
    };

    const getUsers = async () => {
        try {
            // Fetch users and permissions in parallel
            const [userData, permissionsData] = await Promise.all([
                client.getUsers(),
                client.getContactPermissions(currentUser.user_id),
            ]);

            // Extract granted user IDs from permissions
            const grantedUserIds = new Set();
            permissionsData.results.forEach((perm) => {
                grantedUserIds.add(perm.user_id);
            });

            // Filter users based on granted permissions only
            const filteredUsers = userData?.results.filter((user) => {
                const isGranted = grantedUserIds.has(user.user_id);
                return (
                    isGranted &&
                    user.user_id !== currentUser.user_id &&
                    user.is_active === true
                );
            });

            // Map users and assign random colors
            const userListWithColors = filteredUsers.map((user) => ({
                user,
                randomColor: `hsl(${Math.random() * 360}, 70%, 50%)`,
            }));

            // Update state
            setUsersList(userListWithColors);
            setUsersListOriginal(userListWithColors);
        } catch (error) {
            console.error("Error fetching users or permissions:", error);
        }
    };

    const selectUserFromSearchBar = (userId: string) => {
        const userToAddToList = usersList.find(
            (u) => u.user.user_id === userId
        );
        setShownList((prevList) => [...prevList, userToAddToList]);
        setUsersList((prevList) =>
            prevList.filter((el) => el.user.user_id !== userId)
        );
        handleChange(userId);
    };

    useEffect(() => {
        getUsers();
    }, []);

    useEffect(() => {
        setSharedMeetings([]);
        activeCalendars.forEach((cal) => {
            getMeetings(cal);
        });
    }, [activeCalendars]);

    useEffect(() => {
        const copy = [];

        sharedMeetings.forEach((arr) => {
            arr.forEach((el) => {
                copy.push(el);
            });
        });
        setOriginalMeetings(copy);
    }, [sharedMeetings]);

    const resetButton = (
        <button
            className={`w-5/6 bg-gray-700 hover:bg-black text-white rounded-full text-sm py-1 ${
                activeCalendars.length === 1 &&
                activeCalendars.includes(currentUser.user_id) &&
                "hidden"
            }`}
            onClick={reinitialize}
        >
            <span className="w-11/12 inherit">
                {intl.formatMessage({ id: "calendar.reset" })}
            </span>
        </button>
    );

    return (
        <div className="w-full pt-10 px-1 h-full mt-10">
            {/* <h3 className="font-bold text-left pl-2 pb-1">Calendriers</h3> */}

            {!isOnline && (
                <div className="flex flex-col gap-2 h-full items-center mt-20 px-5 text-center">
                    <div className="text-xxl text-gray-400">
                        <FiWifiOff />
                    </div>
                    <div className="text-sm text-gray-500">
                        {intl.formatMessage({ id: "calendar.offline" })}
                    </div>
                    {resetButton}
                </div>
            )}

            {isOnline && (
                <>
                    <div className="w-full pr-2">
                        <UserSearch
                            placeholder="calendar.searchColleague"
                            sharedCalUsersList={usersList}
                            usersList={null}
                            handleSelect={selectUserFromSearchBar}
                        />
                    </div>
                    <ul className="text-left flex flex-col gap-2 text-sm pt-5 overflow-hidden overflow-y-auto h-full w-full pb-10 pr-2">
                        <div
                            className={`py-1 px-2  flex justify-left gap-2 items-center`}
                            id={currentUser.user_id}
                        >
                            <input
                                type="checkbox"
                                checked={activeCalendars.includes(
                                    currentUser.user_id
                                )}
                                className={`hover:bg-gray-100 rounded-full text-xs mb-1`}
                                onChange={() =>
                                    handleChange(currentUser.user_id)
                                }
                                disabled={calendarLoading}
                            />
                            <div>
                                {" "}
                                {intl.formatMessage({
                                    id: "calendar.myCalendar",
                                })}
                            </div>
                        </div>
                        {shownList?.map((el) => {
                            return (
                                <div
                                    key={el.user.user_id}
                                    className={`py-1 px-2 disabled:text-gray-200 disabled:border-gray-200 flex justify-between items-center flex-row items-center`}
                                    id={el.user.user_id}
                                >
                                    <div
                                        className="px-2 py-2 rounded-sm"
                                        style={{
                                            background:
                                                activeCalendars.includes(
                                                    el.user.user_id
                                                )
                                                    ? el.randomColor ||
                                                      "transparent" // Fallback to transparent if el.randomColor is not valid
                                                    : "transparent", // Fallback background color if not active
                                        }}
                                    ></div>

                                    <span className="text-left w-full ml-2">
                                        {el.user.first_name} {el.user.last_name}
                                    </span>
                                    <button
                                        className=" px-2 py-1 hover:bg-gray-100 rounded-full text-base"
                                        onClick={() => handleRemoveUser(el)}
                                        disabled={calendarLoading}
                                    >
                                        x
                                    </button>
                                </div>
                            );
                        })}
                    </ul>
                </>
            )}
            {resetButton}
        </div>
    );
};

export default SharedCalendarList;
